// R116 #5 — the ONE client rule for "is this a video?" (it was copied into six
// pages with three different regexes). Pure, no imports: the Lightbox, the
// media strip and the upload queue all read it. The server's twin is
// lib/evidenceMedia.js (isVideoUrl / isVideoFile) — keep them in step.

/** What every evidence picker accepts: photos AND video, several at once. */
export const EVIDENCE_ACCEPT = 'image/*,video/*';

const VIDEO_URL_RE = /\.(mp4|mov|m4v|webm|3gp|3g2|qt)(\?|#|$)/i;
const VIDEO_NAME_RE = /\.(mp4|mov|m4v|webm|3gp|3g2|qt|mkv|avi)$/i;
const IMAGE_NAME_RE = /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?)$/i;

/** A stored media url is a video when it sits under /video/ or carries a video extension. */
export function isVideoUrl(u: unknown): boolean {
  const s = String(u == null ? '' : u);
  if (!s) return false;
  return /\/video\//.test(s) || VIDEO_URL_RE.test(s);
}

/** A picked file is a video: its type says so, or (empty / generic type) its extension does. */
export function isVideoFile(f: { type?: string; name?: string } | null | undefined): boolean {
  if (!f) return false;
  const t = String(f.type || '').toLowerCase();
  if (t.startsWith('video/')) return true;
  if (t.startsWith('image/')) return false;
  return VIDEO_NAME_RE.test(String(f.name || ''));
}

/** A picked / dropped / pasted file is evidence media (photo or video) — never a PDF, a doc, a zip. */
export function isMediaFile(f: { type?: string; name?: string } | null | undefined): boolean {
  if (!f) return false;
  const t = String(f.type || '').toLowerCase();
  if (t.startsWith('image/') || t.startsWith('video/')) return true;
  if (t && t !== 'application/octet-stream') return false;
  const n = String(f.name || '');
  return VIDEO_NAME_RE.test(n) || IMAGE_NAME_RE.test(n);
}

/** "3 photos + 1 video" for a pick (counts by isVideoFile). */
export function pickSummary(files: Array<{ type?: string; name?: string }>): string {
  const v = files.filter(isVideoFile).length;
  const p = files.length - v;
  return [p ? `${p} photo${p === 1 ? '' : 's'}` : '', v ? `${v} video${v === 1 ? '' : 's'}` : ''].filter(Boolean).join(' + ') || 'nothing';
}

/** Human size: 820 KB / 4.2 MB / 140 MB. */
export function fmtBytes(n: number): string {
  const b = Math.max(0, Number(n) || 0);
  if (b >= 1048576) return `${(b / 1048576).toFixed(b >= 104857600 ? 0 : 1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
}
